"use client";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, FilterX, Pencil, Play, Plus, RotateCcw, Trash2, XCircle, Pause } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import {
  Button,
  ListCount,
  ListSurface,
  ListSurfaceBody,
  ListSurfaceFooter,
  ListSurfaceHeader,
  ListToolbar,
  ListToolbarGroup,
  SkeletonTableRow,
  ToolbarIconButton,
  WorkspacePage,
} from "@/components/ds";
import { tableHeaderSearchInputClass, tableHeaderSelectTriggerClass, ThemedSelect } from "@/components/ui-system/select";
import { deleteOtaCampaign, listOtaCampaigns, retryFailedOtaJobs, updateOtaCampaign } from "@/lib/api/endpoints/ota";
import type { OtaCampaignDto } from "@/lib/api/types/ota";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
];

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

function statusBadgeClass(status: string): string {
  if (status === "active") return "border-success/25 bg-success/10 text-success";
  if (status === "paused") return "border-warning/25 bg-warning/10 text-warning";
  if (status === "cancelled") return "border-error/25 bg-error/10 text-error";
  return "border-card-border bg-surface-secondary text-muted";
}

export function OtaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const canCreate = hasModulePermission(user, permissionMap, "ota", "create");
  const canEdit = hasModulePermission(user, permissionMap, "ota", "edit");
  const canDelete = hasModulePermission(user, permissionMap, "ota", "delete");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(10);
  const debouncedSearch = useDebouncedValue(search, 300);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, pageSize]);

  const query = useQuery({
    queryKey: ["ota", "campaigns", debouncedSearch, statusFilter, page, pageSize],
    queryFn: () =>
      listOtaCampaigns({
        limit: pageSize,
        offset: page * pageSize,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter || undefined,
      }),
    staleTime: 20_000,
    placeholderData: (previousData) => previousData,
  });

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, page * pageSize + rows.length);
  const filtersActive = Boolean(search.trim() || statusFilter);

  const tableSummary = useMemo(
    () => (total === 0 ? "No campaigns" : `Showing ${from}-${to} of ${total}`),
    [from, to, total],
  );

  const statusMutation = useMutation({
    mutationFn: ({ campaignId, status }: { campaignId: string; status: string }) =>
      updateOtaCampaign(campaignId, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ota"] }),
  });

  const retryMutation = useMutation({
    mutationFn: retryFailedOtaJobs,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ota"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOtaCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ota"] }),
  });

  const actionPending = statusMutation.isPending || retryMutation.isPending || deleteMutation.isPending;

  function nextToggleStatus(campaign: OtaCampaignDto): string {
    return campaign.status === "paused" ? "active" : "paused";
  }

  return (
    <WorkspacePage className="max-w-[110rem]">
      <div className="space-y-ds-5">
        <ListSurface>
          <ListSurfaceHeader
            title="OTA campaign listing"
            description="Search and review firmware rollouts across assigned data loggers."
            meta={<ListCount total={total} noun={total === 1 ? "campaign" : "campaigns"} />}
            controls={
              <ListToolbar>
                <ListToolbarGroup grow className="lg:flex-[0_1_42%]">
                  <input
                    className={tableHeaderSearchInputClass}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search version, description, or status..."
                  />
                </ListToolbarGroup>
                <ListToolbarGroup>
                  <ThemedSelect
                    value={statusFilter}
                    ariaLabel="OTA status"
                    options={STATUS_OPTIONS}
                    onChange={setStatusFilter}
                    triggerClassName={tableHeaderSelectTriggerClass}
                    className="min-w-[160px]"
                  />
                </ListToolbarGroup>
                {filtersActive ? (
                  <ToolbarIconButton
                    icon={FilterX}
                    label="Clear filters"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("");
                    }}
                  />
                ) : null}
                {canCreate ? (
                  <ToolbarIconButton icon={Plus} label="Create OTA" variant="primary" onClick={() => navigate("/ota/new")} />
                ) : null}
              </ListToolbar>
            }
          />

          <ListSurfaceBody className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Version</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Target</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Status</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Jobs</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Created</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-right text-micro font-semibold uppercase tracking-wider text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {query.isLoading ? (
                  <tr>
                    <td className="px-ds-4 py-ds-4" colSpan={6}>
                      {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={6} />)}
                    </td>
                  </tr>
                ) : query.isError ? (
                  <tr>
                    <td className="px-ds-4 py-ds-6 text-caption text-error" colSpan={6}>
                      {query.error instanceof Error ? query.error.message : "Failed to load OTA campaigns."}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-ds-4 py-ds-10 text-center text-caption text-muted" colSpan={6}>
                      {filtersActive ? "No OTA campaigns match the current filters." : "No OTA campaigns yet."}
                    </td>
                  </tr>
                ) : (
                  rows.map((campaign) => (
                    <tr key={campaign.id} className="bg-surface-primary">
                      <td className="border-b border-card-border px-ds-4 py-ds-3">
                        <div className="font-semibold text-foreground">{campaign.version}</div>
                        {campaign.description ? (
                          <div className="mt-1 max-w-[22rem] truncate text-caption text-muted">{campaign.description}</div>
                        ) : null}
                      </td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3 text-caption capitalize text-muted">
                        {campaign.target_type}
                      </td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3">
                        <span className={`inline-flex rounded-full border px-ds-2 py-1 text-micro font-semibold uppercase ${statusBadgeClass(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3 text-caption text-foreground">
                        <span className="font-semibold">{campaign.total_jobs}</span>
                        <span className="ml-ds-2 text-muted">
                          {campaign.installed_jobs} installed · {campaign.failed_jobs} failed
                        </span>
                      </td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3 text-caption text-muted">
                        {formatDate(campaign.created_at)}
                      </td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3">
                        <div className="flex flex-wrap justify-end gap-ds-2">
                          <Link
                            to={`/ota/${campaign.id}`}
                            className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-card-border bg-surface-primary px-[10px] text-muted transition-colors hover:bg-surface-hover hover:text-foreground"
                            title={`View OTA ${campaign.version}`}
                          >
                            <Eye size={14} />
                          </Link>
                          {canEdit ? (
                            <>
                              <Link
                                to={`/ota/${campaign.id}/edit`}
                                className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-primary/15 bg-primary/10 px-[10px] text-primary transition-colors hover:border-primary/25 hover:bg-primary/15"
                                title={`Edit OTA ${campaign.version}`}
                              >
                                <Pencil size={14} />
                              </Link>
                              {campaign.status !== "cancelled" && campaign.status !== "completed" ? (
                                <button
                                  type="button"
                                  disabled={actionPending}
                                  className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-warning/25 bg-warning/10 px-[10px] text-warning transition-colors hover:bg-warning/15 disabled:opacity-50"
                                  title={campaign.status === "paused" ? "Resume rollout" : "Pause rollout"}
                                  onClick={() => statusMutation.mutate({ campaignId: campaign.id, status: nextToggleStatus(campaign) })}
                                >
                                  {campaign.status === "paused" ? <Play size={14} /> : <Pause size={14} />}
                                </button>
                              ) : null}
                              {campaign.status !== "cancelled" && campaign.status !== "completed" ? (
                                <button
                                  type="button"
                                  disabled={actionPending}
                                  className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-error/25 bg-error/10 px-[10px] text-error transition-colors hover:bg-error/15 disabled:opacity-50"
                                  title="Cancel rollout"
                                  onClick={() => {
                                    if (confirm(`Cancel OTA "${campaign.version}"? Pending jobs will stop receiving offers.`)) {
                                      statusMutation.mutate({ campaignId: campaign.id, status: "cancelled" });
                                    }
                                  }}
                                >
                                  <XCircle size={14} />
                                </button>
                              ) : null}
                              {campaign.failed_jobs > 0 ? (
                                <button
                                  type="button"
                                  disabled={actionPending}
                                  className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-primary/15 bg-primary/10 px-[10px] text-primary transition-colors hover:bg-primary/15 disabled:opacity-50"
                                  title="Retry failed jobs"
                                  onClick={() => retryMutation.mutate(campaign.id)}
                                >
                                  <RotateCcw size={14} />
                                </button>
                              ) : null}
                            </>
                          ) : (
                            null
                          )}
                          {canDelete ? (
                            <button
                              type="button"
                              disabled={actionPending}
                              className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-error/25 bg-error/10 px-[10px] text-error transition-colors hover:bg-error/15 disabled:opacity-50"
                              title={`Delete OTA ${campaign.version}`}
                              onClick={() => {
                                if (confirm(`Delete OTA campaign "${campaign.version}"? This removes its device job history.`)) {
                                  deleteMutation.mutate(campaign.id);
                                }
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          ) : null}
                          {!canEdit && !canDelete ? (
                            <span className="text-caption text-muted">View only</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ListSurfaceBody>

          <ListSurfaceFooter
            left={
              <label className="flex items-center gap-ds-2">
                <span className="text-caption text-muted">Rows per page</span>
                <ThemedSelect
                  value={String(pageSize)}
                  ariaLabel="Rows per page"
                  options={PAGE_SIZE_OPTIONS.map((size) => ({ value: String(size), label: String(size) }))}
                  onChange={(nextValue) => {
                    setPageSize(Number(nextValue) as (typeof PAGE_SIZE_OPTIONS)[number]);
                    setPage(0);
                  }}
                  triggerClassName={tableHeaderSelectTriggerClass}
                  className="min-w-[84px]"
                />
              </label>
            }
            right={
              <>
                <span className="text-caption text-muted">{tableSummary}</span>
                <Button type="button" variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))}>
                  Previous
                </Button>
                <span className="text-caption text-muted">Page {page + 1} / {pageCount}</span>
                <Button type="button" variant="secondary" size="sm" disabled={page + 1 >= pageCount} onClick={() => setPage((current) => current + 1)}>
                  Next
                </Button>
              </>
            }
          />
        </ListSurface>
      </div>
    </WorkspacePage>
  );
}
